import { invoiceAmounts } from '../../utils/invoiceAmounts';
import { formatRupiah } from '../../utils/printHelper';
import { SignedAmountInput } from '../SignedAmountInput';
import { InvoiceAmountSummary } from '../invoice/InvoiceAmountSummary';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Edit } from 'lucide-react';

export function EditDeductionsDialog({ editAdminBank, editAmount, editDeductionsTarget, editLoading, editPembulatan, editPphPercentage, editPphType, handleSaveDeductions, setEditAdminBank, setEditAmount, setEditPembulatan, setEditPphPercentage, setEditPphType, setShowEditDeductions, showEditDeductions }) {
  return (
    <Dialog open={showEditDeductions} onOpenChange={setShowEditDeductions}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Data Pembayaran</DialogTitle>
        </DialogHeader>
        {editDeductionsTarget && (
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg text-sm">
              <p className="font-semibold">{editDeductionsTarget.data.invoice_number || editDeductionsTarget.data.request_number}</p>
              <p className="text-slate-500">{editDeductionsTarget.type === 'internal' ? (editDeductionsTarget.data?.category === 'operasional' ? 'Operasional Payment Request' : 'Internal Payment Request') : 'Invoice'}</p>
              {editDeductionsTarget.type === 'invoice' && (
                <p className="mt-1 text-slate-600">Total Tagihan: <span className="font-mono font-bold">Rp {formatRupiah(invoiceAmounts(editDeductionsTarget.data).totalTagihan)}</span></p>
              )}
            </div>

            {/* Nilai Pengajuan - only for internal */}
            {editDeductionsTarget.type === 'internal' && (
              <div className="space-y-2">
                <Label className="text-emerald-700 font-medium">Nilai Pengajuan (Rp)</Label>
                <Input type="number" value={editAmount} onChange={(e) => setEditAmount(parseFloat(e.target.value) || 0)} min="0" step="1000" className="w-full font-mono" data-testid="edit-amount" />
              </div>
            )}

            {/* Pembulatan - for invoice */}
            {editDeductionsTarget.type === 'invoice' && (
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Pembulatan (Rp)</Label>
                <SignedAmountInput value={editPembulatan} onChange={setEditPembulatan} className="w-48 font-mono" data-testid="edit-pembulatan" />
                <p className="text-xs text-slate-500">Koreksi pada total tagihan, tidak mempengaruhi DPP. Boleh minus.</p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-rose-700 font-medium">Jenis PPh</Label>
              <select
                className="flex h-9 w-full rounded-md border px-3 py-1 text-sm"
                value={editPphType}
                onChange={(e) => {
                  setEditPphType(e.target.value);
                  if (e.target.value === 'PPh21') setEditPphPercentage(5);
                  else if (e.target.value === 'PPh23') setEditPphPercentage(2);
                  else if (e.target.value === 'PPh4(2)') setEditPphPercentage(10);
                  else setEditPphPercentage(0);
                }}
                data-testid="edit-pph-type"
              >
                <option value="">Tanpa PPh</option>
                <option value="PPh21">PPh 21</option>
                <option value="PPh23">PPh 23</option>
                <option value="PPh4(2)">PPh 4 ayat 2</option>
              </select>
            </div>
            {editPphType && (
              <div className="space-y-2">
                <Label>Persentase PPh (%)</Label>
                <Input type="number" value={editPphPercentage} onChange={(e) => setEditPphPercentage(parseFloat(e.target.value) || 0)} min="0" max="100" step="0.1" className="w-32" data-testid="edit-pph-pct" />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-blue-700 font-medium">Admin Bank (Rp)</Label>
              <Input type="number" value={editAdminBank} onChange={(e) => setEditAdminBank(parseFloat(e.target.value) || 0)} min="0" step="1000" className="w-48" data-testid="edit-admin-bank" />
            </div>

            {/* Summary for internal */}
            {editDeductionsTarget.type === 'internal' && (
              <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg space-y-1 text-sm">
                <div className="flex justify-between"><span>Nilai Pengajuan</span><span className="font-mono">Rp {(editAmount || 0).toLocaleString('id-ID')}</span></div>
                {editPphType && editPphPercentage > 0 && (
                  <div className="flex justify-between text-rose-600"><span>PPh ({editPphType} {editPphPercentage}%)</span><span className="font-mono">- Rp {((editAmount || 0) * (editPphPercentage / 100)).toLocaleString('id-ID')}</span></div>
                )}
                {editAdminBank > 0 && (
                  <div className="flex justify-between text-blue-600"><span>Admin Bank</span><span className="font-mono">- Rp {editAdminBank.toLocaleString('id-ID')}</span></div>
                )}
                <div className="flex justify-between font-bold border-t pt-1">
                  <span>Nominal Bayar</span>
                  <span className="font-mono text-emerald-700">Rp {((editAmount || 0) - ((editAmount || 0) * ((editPphType && editPphPercentage > 0) ? editPphPercentage / 100 : 0)) - (editAdminBank || 0)).toLocaleString('id-ID')}</span>
                </div>
              </div>
            )}

            {/* Preview of the invoice after this edit - same breakdown as the voucher */}
            {editDeductionsTarget.type === 'invoice' && (
              <InvoiceAmountSummary
                invoice={editDeductionsTarget.data}
                overrides={{
                  pembulatan: editPembulatan || 0,
                  pphType: editPphType || null,
                  pphPercentage: editPphPercentage || 0,
                  adminBank: editAdminBank || 0,
                }}
                className="bg-blue-50 border-blue-200"
                title="Nominal Setelah Edit"
              />
            )}

            <Button onClick={handleSaveDeductions} className="w-full bg-blue-600 hover:bg-blue-700" disabled={editLoading} data-testid="save-deductions-btn">
              {editLoading ? 'Menyimpan...' : 'Simpan Perubahan'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
